import { useStorytellClient } from "@storytell/client";
import { MoveAssetsRequestConfig } from "@storytell/client";
import type { AssetId, Project, ProjectId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StDialog, StDialogCloseButton, StDialogTitle } from "@storytell/ui/dialog";
import { TbArrowRight, TbCheck } from "solid-icons/tb";
import { For, Show, createSignal } from "solid-js";
import { useWire } from "~/wire";

type ViewState = "select" | "success";

interface MoveAssetsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedAssetIds: AssetId[];
  currentProjectId: ProjectId;
  onSuccess?: () => void;
}

export function MoveAssetsModal(props: MoveAssetsModalProps) {
  const [view, setView] = createSignal<ViewState>("select");
  const [selectedProjectId, setSelectedProjectId] = createSignal<ProjectId | null>(null);
  const [moving, setMoving] = createSignal(false);
  const [destinationProject, setDestinationProject] = createSignal<Project | null>(null);
  const [movedAssetCount, setMovedAssetCount] = createSignal<number>(0);

  const wire = useWire();
  const client = useStorytellClient();

  // Get all projects except the current one
  const availableProjects = () => {
    return wire.projects.all().filter((p: Project) => p.id !== props.currentProjectId);
  };

  const handleProjectSelect = (projectId: ProjectId) => {
    setSelectedProjectId(projectId);
  };

  const handleConfirmMove = async () => {
    const destProjectId = selectedProjectId();
    if (!destProjectId) return;

    setMoving(true);

    try {
      const request = MoveAssetsRequestConfig.withBearerToken(wire.auth.token());
      const moveRequest = {
        assets: props.selectedAssetIds.map((assetId) => ({
          assetId,
          projectId: destProjectId,
        })),
      };

      const result = await client.fetch(request, undefined, moveRequest);

      if (result.isErr()) {
        console.error("Failed to move assets", result.error);
        return;
      }

      // Store destination project for success message
      const destProject = wire.projects.get(destProjectId);
      if (destProject) {
        setDestinationProject(destProject);
      }

      // Store the count before selection is cleared
      setMovedAssetCount(props.selectedAssetIds.length);

      // Refresh assets to remove moved ones from current view
      await wire.assets.refetch();

      // Show success view
      setView("success");

      // Call success callback
      props.onSuccess?.();
    } catch (error) {
      console.error("Failed to move assets", error);
    } finally {
      setMoving(false);
    }
  };

  const handleSwitchToDestination = async () => {
    const destProjectId = selectedProjectId();
    if (!destProjectId) return;

    try {
      const context = wire.userContext.get();
      if (!context) return;

      await wire.userContext.switchContext({
        tenantId: context.tenantId,
        projectId: destProjectId,
        organizationId: context.organizationId,
        workspaceId: context.workspaceId,
      });

      setTimeout(() => {
        // Open knowledge panel on all knowledge asset view
        wire.knowledgeUi.setActiveView("all");
        wire.knowledgeUi.setActiveTab("assets");
        wire.knowledgeUi.open();
      }, 500);

      handleClose();
    } catch (error) {
      console.error("Failed to switch project", error);
    }
  };

  const handleStayOnCurrent = () => {
    handleClose();
  };

  const handleClose = () => {
    props.onOpenChange(false);
    // Reset state after modal closes
    setTimeout(() => {
      setView("select");
      setSelectedProjectId(null);
      setDestinationProject(null);
      setMovedAssetCount(0);
    }, 200);
  };

  const assetCount = () => (view() === "success" ? movedAssetCount() : props.selectedAssetIds.length);

  return (
    <StDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      content={
        <Show
          when={view() === "select"}
          fallback={
            <>
              <StDialogTitle>Assets Moved Successfully!</StDialogTitle>
              <StDialogCloseButton />

              <div class="space-y-4">
                <div class="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div class="flex items-start gap-3">
                    <TbCheck class="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div class="flex-1">
                      <p class="text-sm text-green-900 dark:text-green-100">
                        {assetCount()} asset{assetCount() === 1 ? " has" : "s have"} been successfully moved to{" "}
                        <span class="font-semibold">{destinationProject()?.name || "the selected project"}</span>.
                      </p>
                    </div>
                  </div>
                </div>

                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Would you like to go to the "{destinationProject()?.name}" project to view your moved assets?
                </p>

                <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <StButton onClick={handleStayOnCurrent}>No, Stay Here</StButton>
                  <StButton
                    onClick={handleSwitchToDestination}
                    icon={TbArrowRight}
                    class="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Yes, Go to Project
                  </StButton>
                </div>
              </div>
            </>
          }
        >
          <>
            <StDialogTitle>
              Move {assetCount()} Asset{assetCount() === 1 ? "" : "s"} to Project
            </StDialogTitle>
            <StDialogCloseButton />

            <div class="space-y-4">
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Select a destination project to move {assetCount()} asset
                {assetCount() === 1 ? "" : "s"} from the current project.
                <br />
                The assets will not be available in the current project after the move.
              </p>

              <div class="space-y-2">
                <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">Available Projects</h3>
                <div class="grid grid-cols-1 gap-2 max-h-[400px] overflow-y-auto">
                  <Show
                    when={availableProjects().length > 0}
                    fallback={
                      <div class="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                        No other projects available
                      </div>
                    }
                  >
                    <For each={availableProjects()}>
                      {(project) => (
                        <button
                          type="button"
                          onClick={() => handleProjectSelect(project.id)}
                          class="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                          classList={{
                            "border-blue-500 dark:border-blue-500 bg-blue-50 dark:bg-blue-950/20":
                              selectedProjectId() === project.id,
                          }}
                        >
                          <div class="flex-1 min-w-0">
                            <div class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {project.name}
                            </div>
                            <Show when={project.description}>
                              <div class="text-xs text-gray-500 dark:text-gray-400 truncate">{project.description}</div>
                            </Show>
                          </div>
                          <Show when={selectedProjectId() === project.id}>
                            <TbCheck class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 ml-2" />
                          </Show>
                        </button>
                      )}
                    </For>
                  </Show>
                </div>
              </div>

              <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <StButton onClick={handleClose} disabled={moving()}>
                  Cancel
                </StButton>
                <StButton
                  onClick={handleConfirmMove}
                  disabled={!selectedProjectId() || moving() || availableProjects().length === 0}
                  icon={TbArrowRight}
                  class="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {moving() ? "Moving..." : `Move ${assetCount()} Asset${assetCount() === 1 ? "" : "s"}`}
                </StButton>
              </div>
            </div>
          </>
        </Show>
      }
    />
  );
}
